import React, { useEffect, useRef } from "react";
import gsap from "gsap";
import icon from "../../assets/icon.png";

import image1 from "../../assets/slides/1.png";
import image2 from "../../assets/slides/2.png";
import image3 from "../../assets/slides/3.png";
import image4 from "../../assets/slides/4.png";
import image5 from "../../assets/slides/5.png";

const images = [image1, image2, image3, image4, image5];

const MarqueeRow = ({ direction = "left", speed = 30, onHoverRef }) => {
  const rowRef = useRef(null);
  const animRef = useRef(null);
  const items = [...images, ...images, ...images]; // triple for seamless loop

  useEffect(() => {
    const row = rowRef.current;
    if (!row) return;

    const totalWidth = row.scrollWidth / 3;
    const startX = direction === "left" ? 0 : -totalWidth;
    const endX = direction === "left" ? -totalWidth : 0;

    gsap.set(row, { x: startX });

    animRef.current = gsap.to(row, {
      x: endX,
      duration: speed,
      ease: "none",
      repeat: -1,
      modifiers: {
        x: gsap.utils.unitize((x) => {
          const parsed = parseFloat(x);
          if (direction === "left") {
            return parsed <= -totalWidth ? parsed + totalWidth : parsed;
          } else {
            return parsed >= 0 ? parsed - totalWidth : parsed;
          }
        }),
      },
    });

    // expose pause/play to parent via ref
    if (onHoverRef) {
      onHoverRef.current = {
        pause: () => animRef.current?.pause(),
        play: () => animRef.current?.play(),
      };
    }

    return () => animRef.current?.kill();
  }, []);

  return (
    <div className="overflow-hidden w-full">
      <div
        ref={rowRef}
        style={{ display: "flex", gap: "16px", willChange: "transform" }}
      >
        {items.map((img, i) => (
          <div
            key={i}
            style={{
              flexShrink: 0,
              width: "260px",
              height: "300px",
              borderRadius: "16px",
              overflow: "hidden",
              position: "relative",
            }}
            onMouseEnter={(e) => {
              gsap.to(e.currentTarget.querySelector("img"), {
                scale: 1.08,
                duration: 0.4,
                ease: "power2.out",
              });
              gsap.to(e.currentTarget.querySelector(".overlay"), {
                opacity: 1,
                duration: 0.3,
              });
            }}
            onMouseLeave={(e) => {
              gsap.to(e.currentTarget.querySelector("img"), {
                scale: 1,
                duration: 0.4,
                ease: "power2.out",
              });
              gsap.to(e.currentTarget.querySelector(".overlay"), {
                opacity: 0,
                duration: 0.3,
              });
            }}
          >
            <img
              src={img}
              alt={`feed-${i}`}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                display: "block",
                transformOrigin: "center",
              }}
            />
            {/* Hover overlay */}
            <div
              className="overlay"
              style={{
                position: "absolute",
                inset: 0,
                background:
                  "linear-gradient(to top, rgba(0,0,0,0.55) 0%, transparent 60%)",
                opacity: 0,
                display: "flex",
                alignItems: "flex-end",
                padding: "16px",
              }}
            >
              <div
                style={{
                  width: "32px",
                  height: "32px",
                  borderRadius: "50%",
                  background: "rgba(255,255,255,0.15)",
                  backdropFilter: "blur(6px)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <img src={icon} alt="" style={{ width: "16px", height: "16px" }} />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const Feed = () => {
  const containerRef = useRef(null);
  const row1Ref = useRef(null);
  const row2Ref = useRef(null);
  const headerRef = useRef(null);

  useEffect(() => {
    // Header entrance
    gsap.from(headerRef.current, {
      y: 30,
      opacity: 0,
      duration: 0.8,
      ease: "power3.out",
    });
  }, []);

  const handleMouseEnter = () => {
    row1Ref.current?.pause();
    row2Ref.current?.pause();
  };

  const handleMouseLeave = () => {
    row1Ref.current?.play();
    row2Ref.current?.play();
  };

  return (
    <div
      style={{
        width: "100%",
        paddingTop: "80px",
        paddingBottom: "80px",
        background: "#EEEEEE",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "32px",
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <div
        ref={headerRef}
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "12px",
        }}
      >
        <img src={icon} alt="Instagram" style={{ width: "48px", height: "48px" }} />
        <h1
        className="product-tabs"
          style={{
            fontSize: "clamp(28px, 4vw, 40px)",
            fontWeight: 700,
            color: "#0A0A0A",
            margin: 0,
            letterSpacing: "-0.5px",
          }}
        >
          Instagram Feed
        </h1>
        <div style={{ width: "56px", height: "3px", background: "#0A0A0A", borderRadius: "2px" }} />
      </div>

      {/* Dual marquee rows */}
      <div
        ref={containerRef}
        style={{ width: "100%", display: "flex", flexDirection: "column", gap: "16px" }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <MarqueeRow direction="left" speed={28} onHoverRef={row1Ref} />
        <MarqueeRow direction="right" speed={22} onHoverRef={row2Ref} />
      </div>
    </div>
  );
};

export default Feed;